import React, { useRef } from "react";
import { Box, Typography, Card, CardContent, CardMedia, Chip, IconButton } from "@mui/material";
import { ArrowForwardIos, ArrowBackIos, TrendingUp } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import API from "../../../API";

const FeaturedArticles = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  // Fetch featured articles
  const { data: articlesData, isLoading } = useQuery(
    "featuredArticles",
    () => API.articles.list({ featured: true, per_page: 6 }),
    { staleTime: 300000 }
  );

  const articles = articlesData?.data?.articles || [];

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // Don't render if no featured articles
  if (!isLoading && articles.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "float 8s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px)" },
            "50%": { transform: "translateY(-30px)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          left: "-5%",
          width: "350px",
          height: "350px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(245,87,108,0.15) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "float 10s ease-in-out infinite",
          animationDelay: "1s",
        }}
      />

      <Box sx={{ maxWidth: "1400px", mx: "auto", px: { xs: 2, md: 4 }, position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
              <TrendingUp sx={{ fontSize: 32, color: "#feca57" }} />
              <Typography
                variant="overline"
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                }}
              >
                Featured Insights
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "4rem" },
                color: "#0f172a",
                mb: 2,
                fontFamily:"Moret Variable" ,fontWeight:'600',
              }}
            >
              Trending Articles
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#475569",
                maxWidth: "600px",
                mx: "auto",
                fontSize: { xs: "0.95rem", md: "1.1rem" },
              }}
            >
              Discover the latest insights, strategies, and trends shaping the creator economy
            </Typography>
          </Box>
        </motion.div>

        {/* Articles Carousel */}
        <Box sx={{ position: "relative" }}>
          {/* Navigation Buttons */}
          {articles.length > 2 && (
            <>
              <IconButton
                onClick={() => scroll("left")}
                sx={{
                  position: "absolute",
                  left: { xs: -10, md: -20 },
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  color: "#0f172a",
                  border: "1px solid rgba(0,0,0,0.08)",
                  "&:hover": {
                    background: "#f8fafc",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                  display: { xs: "none", sm: "flex" },
                }}
              >
                <ArrowBackIos sx={{ ml: 0.5 }} />
              </IconButton>
              <IconButton
                onClick={() => scroll("right")}
                sx={{
                  position: "absolute",
                  right: { xs: -10, md: -20 },
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  color: "#0f172a",
                  border: "1px solid rgba(0,0,0,0.08)",
                  "&:hover": {
                    background: "#f8fafc",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                  display: { xs: "none", sm: "flex" },
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            </>
          )}

          {/* Scrollable Container */}
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 3,
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              pb: 2,
              px: { xs: 0, sm: 2 },
            }}
          >
            {isLoading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      minWidth: { xs: 280, sm: 340, md: 380 },
                      height: 420,
                      background: "white",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      borderRadius: "16px",
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  />
                ))
              : articles.map((article, index) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      onClick={() => navigate(`/articles/${article.slug}`)}
                      sx={{
                        minWidth: { xs: 280, sm: 340, md: 380 },
                        height: 420,
                        background: "white",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                        borderRadius: "16px",
                        border: "1px solid rgba(0,0,0,0.06)",
                        cursor: "pointer",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-8px) scale(1.02)",
                          boxShadow: "0 20px 60px rgba(102,126,234,0.2)",
                          border: "1px solid rgba(102,126,234,0.3)",
                          "& .article-image": {
                            transform: "scale(1.1)",
                          },
                          "& .gradient-overlay": {
                            opacity: 0.7,
                          },
                        },
                      }}
                    >
                      {article.featured_image && (
                        <Box sx={{ position: "relative", height: 200, overflow: "hidden" }}>
                          <CardMedia
                            className="article-image"
                            component="img"
                            image={article.featured_image}
                            alt={article.featured_image_alt || article.title}
                            sx={{
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.4s ease",
                            }}
                          />
                          <Box
                            className="gradient-overlay"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: "linear-gradient(to bottom, transparent 0%, rgba(15,52,96,0.8) 100%)",
                              opacity: 0.5,
                              transition: "opacity 0.4s ease",
                            }}
                          />
                          <Chip
                            label="Featured"
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "white",
                              fontWeight: 600,
                              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                            }}
                          />
                        </Box>
                      )}

                      <CardContent sx={{ p: 3, height: 220, display: "flex", flexDirection: "column" }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            mb: 1.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            lineHeight: 1.4,
                            fontSize: { xs: "1.1rem", md: "1.2rem" },
                          }}
                        >
                          {article.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            mb: 2,
                            flexGrow: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            lineHeight: 1.6,
                          }}
                        >
                          {article.excerpt}
                        </Typography>

                        <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {article.category && (
                              <Chip
                                label={article.category}
                                size="small"
                                sx={{
                                  background: "rgba(102,126,234,0.1)",
                                  color: "#667eea",
                                  border: "1px solid rgba(102,126,234,0.2)",
                                  fontSize: "0.75rem",
                                }}
                              />
                            )}
                            {article.read_time_minutes && (
                              <Chip
                                label={`${article.read_time_minutes} min read`}
                                size="small"
                                sx={{
                                  background: "rgba(254,202,87,0.15)",
                                  color: "#d97706",
                                  border: "1px solid rgba(254,202,87,0.3)",
                                  fontSize: "0.75rem",
                                }}
                              />
                            )}
                          </Box>

                          <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                            {formatDate(article.published_at)}
                            {article.author?.name && ` • ${article.author.name}`}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
          </Box>
        </Box>

        {/* View All Button */}
        {articles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Box
                component="button"
                onClick={() => navigate("/articles")}
                sx={{
                  px: { xs: 5, md: 8 },
                  py: { xs: 2, md: 2.5 },
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontWeight: 700,
                  color: "white",
                  background: (theme) => theme.palette.primary.main,
                  border: "none",
                  borderRadius: "50px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                  "&:hover": {
                    background: (theme) => theme.palette.primary.light,
                    transform: "translateY(-4px) scale(1.02)",
                    boxShadow: "0 16px 56px rgba(0,0,0,0.2)",
                  },
                }}
              >
                View All Articles →
              </Box>
            </Box>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

export default FeaturedArticles;